import React, { useState } from 'react';
import { getBreakpoints, useBreakpoint } from '@noths/polaris-client-responsive';
import { Image, LoadingSpinner, ProductImage } from '@noths/polaris-client-ribbons-design-system';
import { Breakpoint } from '@noths/polaris-client-styles';
import dynamic from 'next/dynamic';

import { Modal, styles as modalStyles } from 'src/components/molecules/Modal';
import { ModalLoader } from 'src/components/molecules/ModalLoader';
import { getIconPath } from 'src/utils';
import { PRODUCT_IMAGES_TAB_ID_PREFIX, PRODUCT_IMAGES_TABPANEL_ID_PREFIX } from '../../constants';
import { ThumbnailsContainer } from '../../containers/ThumbnailsContainer';
import { ProductImagesState, SetCurrentImagePayload, ThumbnailOrientation } from '../../types';
import * as styles from '../../styles/ProductImageModal.styles';
import * as productImagesStyles from '../../styles/ProductImages.styles';

const ZoomAndPanWrapper = dynamic(
  async () =>
    (await import(/* webpackChunkName: "ZoomAndPanWrapper" */ './ZoomAndPanWrapper'))
      .ZoomAndPanWrapper,
  { loading: () => <ModalLoader /> },
);

const ICON_CLOSE = getIconPath('icons-24-close.svg');
const IMAGES_ID_PREFIX = 'fullscreen-';

const isOnLargeScreenOrAbove = (breakpoint: Breakpoint) =>
  getBreakpoints({ minBreakpoint: Breakpoint.LARGE }).includes(breakpoint);

export interface ProductImageModalProps {
  images: Image[];
  currentImage: ProductImagesState['currentImage'];
  isModalOpen: ProductImagesState['isModalOpen'];
  closeModal: VoidFunction;
  setCurrentImage: (payload: SetCurrentImagePayload) => void;
}

export const ProductImageModal = ({
  isModalOpen,
  closeModal,
  images,
  currentImage,
  setCurrentImage,
}: ProductImageModalProps) => {
  const [loadedImages, setImageLoaded] = useState<Record<number, boolean>>({});
  const breakpoint = useBreakpoint();
  const orientation = isOnLargeScreenOrAbove(breakpoint)
    ? ThumbnailOrientation.VERTICAL
    : ThumbnailOrientation.HORIZONTAL;

  const multipleImagesToDisplay = images.length > 1;
  const location = 'Expanded image';

  return (
    <>
      <Modal
        isModalOpen={isModalOpen}
        closeModal={() => closeModal()}
        contentLabel="View full screen image"
      >
        <div css={[modalStyles.modalViewport, styles.modalContentContainer]}>
          <div css={styles.fullViewContainer}>
            {multipleImagesToDisplay && (
              <div css={styles.thumbnails}>
                <ThumbnailsContainer
                  orientation={orientation}
                  onThumbnailClick={(imageIndex) =>
                    setCurrentImage({
                      imageIndex: imageIndex,
                      navigationType: 'click',
                      location,
                    })
                  }
                  idPrefix={IMAGES_ID_PREFIX}
                  location={location}
                />
              </div>
            )}
            <div css={styles.imagesContainer}>
              {images.map(({ href: src, title }, i) => {
                return (
                  <div
                    role="tabpanel"
                    aria-labelledby={`${IMAGES_ID_PREFIX}${PRODUCT_IMAGES_TAB_ID_PREFIX}${i}`}
                    id={`${IMAGES_ID_PREFIX}${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
                    css={styles.tabpanel}
                    style={i === currentImage ? {} : { display: 'none' }}
                    key={src}
                  >
                    <ZoomAndPanWrapper shouldDisplay={i === currentImage}>
                      {!loadedImages[i] && <LoadingSpinner css={styles.loadingSpinner} />}
                      <ProductImage
                        src={src}
                        size="original"
                        alt={`${title}${
                          multipleImagesToDisplay ? `, ${`${i + 1} of ${images.length}`}` : ''
                        }`}
                        css={styles.fullScreenImage}
                        onLoad={() => setImageLoaded({ ...loadedImages, [i]: true })}
                      />
                    </ZoomAndPanWrapper>
                  </div>
                );
              })}
              {multipleImagesToDisplay && (
                <p css={productImagesStyles.progressIndicator} aria-hidden="true">
                  {`${currentImage + 1} of ${images.length}`}
                </p>
              )}
            </div>
          </div>
          <button css={styles.modalCloseButton} onClick={() => closeModal()}>
            <img src={ICON_CLOSE} alt="Close" width={24} height={24} />
          </button>
        </div>
      </Modal>
    </>
  );
};
